"use client"
import { useOrderWebSocket } from "@/hooks/use-order-websocket"
import { useRouter } from "next/navigation"
import { joinOrderChannel, leaveOrderChannel, addEventListener } from "@/lib/socket"
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react"
import { toast } from "sonner"
import {
  fetchMenu,
  fetchMenuBebidas,
  fetchBuffete,
  submitOrder,
  iniciarSessaoCliente,
  fetchEstabelecimento,
  effectivePrice,
  type Category,
  type Dish,
  type Buffete,
  type OrderItem,
  type OrderBuffete,
  type OrderStatus,
  type Estabelecimento,
} from "./api"

import {
  generateEncryptedToken,
  validateEncryptedToken,
  saveTokenToStorage,
  getTokenFromStorage,
  saveSessionData,
  getSessionData,
  isTokenExpired,
  isTokenMarkedAsExpired,
  markTokenAsExpired,
  clearTokenExpiredMark,
  redirectToCleanURL,
  forceNewSession,
  isNewScan,
  saveCompletedOrder,
  getCompletedOrders,
  checkTokenExpirationAndCleanup,
  type SessionData,
  type CompletedOrder,
} from "./auth"

import {
  addOrderToDevice,
  updateOrderStatus,
  getActiveOrders,
  getAllDeviceOrders,
  type DeviceOrder,
} from "./device-storage"

interface AppContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
  draftItems: DraftItem[]
  addToDraft: (dish: Dish) => void
  removeDraftItem: (dishId: string) => void
  updateDraftItemQuantity: (dishId: string, quantity: number) => void
  clearDraft: () => void
  draftCount: number
  draftTotal: number
  orderObservacoes: string
  setOrderObservacoes: (value: string) => void
  buffete: Buffete | null
  draftBuffeteQty: number
  addBuffeteToDraft: () => void
  updateDraftBuffeteQuantity: (quantity: number) => void
  removeBuffeteFromDraft: () => void
  showDraft: boolean
  setShowDraft: (show: boolean) => void
  notification: NotificationState
  showNotification: (message: string) => void
  hideNotification: () => void
  playAddSound: () => void
  categories: Category[]
  categoriesBebidas: Category[]
  isLoading: boolean
  error: string | null
  localId: string | null
  setLocalId: (id: string | null) => void
  submitCustomerOrder: (
    customerName: string,
    phoneNumber: string,
    paymentMethod: string,
    dadosFiscais?: { tipo: "pessoal" | "empresa"; nif?: string; nomeEmpresa?: string; endereco?: string },
  ) => Promise<any>
  showCustomerNameModal: boolean
  setShowCustomerNameModal: (show: boolean) => void
  token: string | null
  resetToken: () => void
  activeOrderIds: string[]
  addActiveOrder: (orderId: string) => void
  removeActiveOrder: (orderId: string) => void
  deviceOrders: DeviceOrder[]
  refreshDeviceOrders: () => void
  orderStatus: OrderStatus | null
  fetchOrderStatus: () => Promise<void>
  showOrderStatus: boolean
  setShowOrderStatus: (show: boolean) => void
  liveNotification: { mensagem: string; pedidoId: string; imagem?: string } | null
  mostrarNotificacaoViva: (mensagem: string, pedidoId: string, imagem?: string) => void
  limparNotificacaoViva: () => void
  isValidSession: boolean
  customerAuth: CustomerAuth | null
  setCustomerAuth: (auth: CustomerAuth | null) => void
  isAuthenticated: boolean
  logout: () => void
  isSessionExpired: boolean
  completeOrder: () => void
  isOrderCompleted: boolean
  isWebSocketListening: boolean
  webSocketOrderStatus: OrderStatus | null
  refreshOrderStatus: () => Promise<OrderStatus | null>
  estabelecimentoId: string | null
  completedOrders: CompletedOrder[]
  getCompletedOrdersHistory: () => CompletedOrder[]
  orderHistory: OrderHistory[]
  addToOrderHistory: (order: OrderStatus, customerName: string) => void
  getOrderHistoryFromStorage: () => OrderHistory[]
  currentOrderId: string | null
  setCurrentOrderId: (id: string | null) => void
  acknowledgeOrder: (orderId: string) => void
  isOrderAcknowledged: (orderId?: string | null) => boolean
  estabelecimento: Estabelecimento | null
  setEstabelecimento: (estabelecimento: Estabelecimento | null) => void
  menuId: string | null
}

type Language = "pt" | "en" | "fr"

interface DraftItem {
  dish: Dish
  quantity: number
}

interface NotificationState {
  message: string
  isVisible: boolean
}

interface CustomerAuth {
  name: string
  phoneNumber: string
  clienteId?: string
  lastOrderDate?: string
}

interface OrderHistory {
  orderId: string
  customerName: string
  items: Array<{
    prato: {
      nome: string
      preco: number
    }
    quantidade: number
  }>
  total: number
  status: string
  date: string
  completedAt?: string
}

export const translations: Record<Language, Record<string, string>> = {
  pt: {
    explore: "Explorar Menu",
    ourMenu: "Nosso Menu",
    ourBeverages: "Nossas Bebidas",
    ingredients: "Ingredientes",
    accompaniments: "Acompanhamentos",
    from: "A partir de",
    items: "itens",
    addToDraft: "Adicionar ao Pedido",
    draft: "Pedido",
    viewDraft: "Ver Pedido",
    clearDraft: "Limpar Pedido",
    quantity: "Quantidade",
    total: "Total",
    close: "Fechar",
    poweredBy: "Powered By Synctech",
    itemAdded: "Item adicionado ao pedido!",
    quantityIncreased: "Quantidade aumentada no pedido!",
    added: "Adicionado!",
    removeItem: "Remover item",
    manageItem: "Gerenciar item",
    addMore: "Adicionar mais",
    welcomeTitle1: "Bem-vindo ao Restaurante",
    welcomeSubtitle1: "Descubra a excelência gastronômica da nossa culinária",
    welcomeTitle2: "Sabores Incríveis",
    welcomeSubtitle2: "Uma experiência única de gastronomia com toques contemporâneos",
    welcomeTitle3: "Culinária Autêntica",
    welcomeSubtitle3: "Pratos preparados com ingredientes frescos e técnicas tradicionais",
    decreaseQuantity: "Diminuir quantidade",
    aboutOurCuisine: "Sobre Nossa Culinária",
    cuisineDescription:
      "Cada prato é preparado com ingredientes cuidadosamente selecionados, combinando técnicas tradicionais com toques contemporâneos.",
    qualityPromise:
      "Comprometemo-nos com a excelência em cada detalhe, desde a seleção dos ingredientes até a apresentação final do prato.",
    scanQrCode: "Por favor, escaneie o código QR",
    enterName: "Digite seu nome",
    enterPhone: "Digite seu número de telefone",
    submit: "Enviar Pedido",
    cancel: "Cancelar",
    orderSuccess: "Pedido enviado com sucesso!",
    orderError: "Erro ao enviar pedido. Tente novamente.",
    preparationTime: "Tempo de preparo",
    minutes: "minutos",
    featured: "Destacado",
    nameRequired: "Por favor, digite seu nome",
    phoneRequired: "Por favor, digite seu número de telefone",
    invalidPhone: "Número de telefone inválido",
    processing: "Processando...",
    description: "Descrição",
    moreImages: "Mais imagens",
    orderStatus: "Status do Pedido",
    viewOrderStatus: "Ver Status do Pedido",
    orderNumber: "Número do Pedido",
    orderDate: "Data do Pedido",
    orderItems: "Itens do Pedido",
    orderTotal: "Total do Pedido",
    statusPending: "Pendente",
    statusInProgress: "Em Preparo",
    statusReady: "Pronto",
    statusDelivered: "Entregue",
    statusCancelled: "Cancelado",
    newOrder: "Novo Pedido",
    scanAgain: "Escanear Novamente",
    sessionExpired: "Sessão Expirada",
    scanToOrder: "Escaneie o QR code para fazer um novo pedido",
    sessionRenewed: "Sessão renovada com sucesso!",
    welcomeBack: "Bem-vindo de volta",
    logout: "Sair",
    yourProfile: "Seu Perfil",
    lastOrder: "Último pedido",
    orderCompleted: "Pedido Finalizado",
    thankYou: "Obrigado!",
    enjoyMeal: "Aproveite sua refeição!",
    newOrderQuestion: "Deseja fazer um novo pedido?",
    startNewOrder: "Iniciar Novo Pedido",
    closeSession: "Fechar Sessão",
  },
  en: {
    explore: "Explore Menu",
    ourMenu: "Our Menu",
    ourBeverages: "Our Beverages",
    ingredients: "Ingredients",
    accompaniments: "Side Dishes",
    from: "Starting at",
    items: "items",
    addToDraft: "Add to Order",
    draft: "Order",
    viewDraft: "View Order",
    clearDraft: "Clear Order",
    quantity: "Quantity",
    total: "Total",
    close: "Close",
    poweredBy: "Powered By SyncTech",
    itemAdded: "Item added to order!",
    quantityIncreased: "Quantity increased in order!",
    added: "Added!",
    removeItem: "Remove item",
    manageItem: "Manage item",
    addMore: "Add more",
    welcomeTitle1: "Welcome to the Restaurant",
    welcomeSubtitle1: "Discover the gastronomic excellence of our cuisine",
    welcomeTitle2: "Amazing Flavors",
    welcomeSubtitle2: "A unique experience of gastronomy with contemporary touches",
    welcomeTitle3: "Authentic Cuisine",
    welcomeSubtitle3: "Dishes prepared with fresh ingredients and traditional techniques",
    decreaseQuantity: "Decrease quantity",
    aboutOurCuisine: "About Our Cuisine",
    cuisineDescription:
      "Each dish is prepared with carefully selected ingredients, combining traditional techniques with contemporary touches.",
    qualityPromise:
      "We are committed to excellence in every detail, from the selection of ingredients to the final presentation of the dish.",
    scanQrCode: "Please scan the QR code to access the menu",
    enterName: "Enter your name",
    enterPhone: "Enter your phone number",
    submit: "Submit Order",
    cancel: "Cancel",
    orderSuccess: "Order submitted successfully!",
    orderError: "Error submitting order. Please try again.",
    preparationTime: "Preparation time",
    minutes: "minutes",
    featured: "Featured",
    nameRequired: "Please enter your name",
    phoneRequired: "Please enter your phone number",
    invalidPhone: "Invalid phone number",
    processing: "Processing...",
    description: "Description",
    moreImages: "More images",
    orderStatus: "Order Status",
    viewOrderStatus: "View Order Status",
    orderNumber: "Order Number",
    orderDate: "Order Date",
    orderItems: "Order Items",
    orderTotal: "Order Total",
    statusPending: "Pending",
    statusInProgress: "In Progress",
    statusReady: "Ready",
    statusDelivered: "Delivered",
    statusCancelled: "Cancelled",
    newOrder: "New Order",
    scanAgain: "Scan Again",
    sessionExpired: "Session Expired",
    scanToOrder: "Scan the QR code to place a new order",
    sessionRenewed: "Session renewed successfully!",
    welcomeBack: "Welcome back",
    logout: "Logout",
    yourProfile: "Your Profile",
    lastOrder: "Last order",
    orderCompleted: "Order Completed",
    thankYou: "Thank you!",
    enjoyMeal: "Enjoy your meal!",
    newOrderQuestion: "Would you like to place a new order?",
    startNewOrder: "Start New Order",
    closeSession: "Close Session",
  },
  fr: {
    explore: "Explorer le Menu",
    ourMenu: "Notre Menu",
    ourBeverages: "Notre boissons",
    ingredients: "Ingrédients",
    accompaniments: "Accompagnements",
    from: "À partir de",
    items: "articles",
    addToDraft: "Ajouter à la Commande",
    draft: "Commande",
    viewDraft: "Voir la Commande",
    clearDraft: "Effacer la Commande",
    quantity: "Quantité",
    total: "Total",
    close: "Fermer",
    poweredBy: "Propulsé par Ceids Tech",
    itemAdded: "Article ajouté à la commande!",
    quantityIncreased: "Quantité augmentée dans la commande!",
    added: "Ajouté!",
    removeItem: "Supprimer l'article",
    manageItem: "Gérer l'article",
    addMore: "Ajouter plus",
    welcomeTitle1: "Bienvenue au Restaurant",
    welcomeSubtitle1: "Découvrez l'excellence gastronomique de notre cuisine",
    welcomeTitle2: "Saveurs Incroyables",
    welcomeSubtitle2: "Une expérience unique de gastronomie avec des touches contemporaines",
    welcomeTitle3: "Cuisine Authentique",
    welcomeSubtitle3: "Plats préparés avec des ingrédients frais et des techniques traditionnelles",
    decreaseQuantity: "Diminuer la quantité",
    aboutOurCuisine: "À Propos de Notre Cuisine",
    cuisineDescription:
      "Chaque plat est préparé avec des ingrédients soigneusement sélectionnés, combinant des techniques traditionnelles avec des touches contemporaines.",
    qualityPromise:
      "Nous nous engageons à l'excellence dans chaque détail, de la sélection des ingrédients à la présentation finale du plat.",
    scanQrCode: "Veuillez scanner le code QR pour accéder au menu",
    enterName: "Entrez votre nom",
    enterPhone: "Entrez votre numéro de téléphone",
    submit: "Envoyer la Commande",
    cancel: "Annuler",
    orderSuccess: "Commande envoyée com sucesso!",
    orderError: "Erreur lors de l'envoi de la commande. Veuillez réessayer.",
    preparationTime: "Temps de préparation",
    minutes: "minutes",
    featured: "En vedette",
    nameRequired: "Veuillez entrer votre nom",
    phoneRequired: "Veuillez entrer votre numéro de téléphone",
    invalidPhone: "Numéro de téléphone invalide",
    processing: "Traitement en cours...",
    description: "Description",
    moreImages: "Plus d'images",
    orderStatus: "Statut de la Commande",
    viewOrderStatus: "Voir le Statut de la Commande",
    orderNumber: "Numéro de Commande",
    orderDate: "Date de Commande",
    orderItems: "Articles de la Commande",
    orderTotal: "Total de la Commande",
    statusPending: "En Attente",
    statusInProgress: "En Préparation",
    statusReady: "Prêt",
    statusDelivered: "Livré",
    statusCancelled: "Annulé",
    newOrder: "Nouvelle Commande",
    scanAgain: "Scanner à Nouveau",
    sessionExpired: "Session Expirée",
    scanToOrder: "Scannez le code QR pour passer uma nova commande",
    sessionRenewed: "Session renouvelée com sucesso!",
    welcomeBack: "Bienvenue à nouveau",
    logout: "Déconnexion",
    yourProfile: "Votre Profil",
    lastOrder: "Dernière commande",
    orderCompleted: "Commande Terminée",
    thankYou: "Merci!",
    enjoyMeal: "Bon appétit!",
    newOrderQuestion: "Souhaitez-vous passer uma nova commande?",
    startNewOrder: "Commencer Nouvelle Commande",
    closeSession: "Fermer Session",
  },
}

const AppContext = createContext<AppContextType | undefined>(undefined)

export function useApp() {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error("useApp must be used inside AppProvider")
  return ctx
}

// Pré-carregar o som para uso mais rápido
let cachedAddSound: HTMLAudioElement | null = null
if (typeof window !== "undefined") {
  try {
    cachedAddSound = new Audio(
      "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/zip-blob/MenuZipado/public/add-sound-sDWdTuxX7yKG0U6N0n6Dj6vEnfcAve.mp3",
    )
    cachedAddSound.volume = 0.3
    cachedAddSound.load()
  } catch (e) {
    console.error("Erro ao criar áudio:", e)
  }
}

// Chaves para localStorage
const ORDER_DATA_KEY = "current_order_data"
const ORDER_HISTORY_KEY = "order_history"
const ACK_COMPLETED_KEY = "ack_completed_orders"

const getInitialAcknowledged = () => {
  if (typeof window === "undefined") return new Set<string>()
  try {
    const raw = localStorage.getItem(ACK_COMPLETED_KEY)
    return raw ? new Set<string>(JSON.parse(raw)) : new Set<string>()
  } catch (e) {
    console.error("[AppContext] erro ao ler ACK init:", e)
    return new Set<string>()
  }
}

// Funções auxiliares de tradução
function translateCategoryName(name: string, lang: string): string {
  return name
}

function translateDishName(name: string, lang: string): string {
  return name
}

export function AppProvider({ children }: { children: ReactNode }) {
  const router = useRouter()
  const acknowledgedCompletedRef = useRef<Set<string>>(getInitialAcknowledged())
  const manuallyClosedOrdersRef = useRef<Set<string>>(new Set())
  // helpers moved inside the provider so they can access the ref
  const [ackVersion, setAckVersion] = useState(0)

  const persistAcknowledged = () => {
    if (typeof window === "undefined") return
    try {
      const arr = Array.from(acknowledgedCompletedRef.current)
      localStorage.setItem(ACK_COMPLETED_KEY, JSON.stringify(arr))
      setAckVersion(v => v + 1)
    } catch (e) {
      console.error("[AppContext] Erro ao persistir acknowledged:", e)
    }
  }

  const restoreAcknowledged = () => {
    if (typeof window === "undefined") return
    try {
      const raw = localStorage.getItem(ACK_COMPLETED_KEY)
      if (!raw) return
      const arr: string[] = JSON.parse(raw)
      acknowledgedCompletedRef.current = new Set(arr)
      setAckVersion(v => v + 1)
    } catch (e) {
      console.error("[AppContext] Erro ao restaurar acknowledged:", e)
    }
  }

  const acknowledgeOrder = (orderId: string) => {
    if (!orderId) return
    acknowledgedCompletedRef.current.add(orderId)
    try {
      const arr = Array.from(acknowledgedCompletedRef.current)
      if (typeof window !== "undefined") localStorage.setItem(ACK_COMPLETED_KEY, JSON.stringify(arr))
      setAckVersion(v => v + 1)
    } catch (e) {
      console.error("[AppContext] Erro ao persistir acknowledged:", e)
    }
  }


  const isOrderAcknowledged = (orderId?: string | null): boolean => {
    if (!orderId) return false
    return acknowledgedCompletedRef.current.has(orderId)
  }

  // restore once on mount
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === ACK_COMPLETED_KEY) {
        restoreAcknowledged()
      }
      if (e.key === ORDER_DATA_KEY) {
        // opcional: re-load order data se necessário
        // loadOrderData()
      }
    }
    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [])

  const [language, setLanguage] = useState<Language>("pt")
  const [draftItems, setDraftItems] = useState<DraftItem[]>([])
  const [orderObservacoes, setOrderObservacoes] = useState("")
  const [showDraft, setShowDraft] = useState(false)
  const [notification, setNotification] = useState<NotificationState>({
    message: "",
    isVisible: false,
  })
  const [categories, setCategories] = useState<Category[]>([])
  const [categoriesBebidas, setCategoriesBebidas] = useState<Category[]>([])
  const [buffete, setBuffete] = useState<Buffete | null>(null)
  const [draftBuffeteQty, setDraftBuffeteQty] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [localId, setLocalId] = useState<string | null>(null)
  const [menuId, setMenuId] = useState<string | null>(null)
  const [estabelecimento, setEstabelecimento] = useState<Estabelecimento | null>(null)
  const [showCustomerNameModal, setShowCustomerNameModal] = useState(false)

  const [token, setToken] = useState<string | null>(null)
  const [activeOrderIds, setActiveOrderIds] = useState<string[]>([])
  const [deviceOrders, setDeviceOrders] = useState<DeviceOrder[]>([])
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null)

  const [orderStatus, setOrderStatus] = useState<OrderStatus | null>(null)
  const [showOrderStatus, setShowOrderStatus] = useState(false)
  const [liveNotification, setLiveNotification] = useState<{ mensagem: string; pedidoId: string; imagem?: string } | null>(null)

  const mostrarNotificacaoViva = useCallback((mensagem: string, pedidoId: string, imagem?: string) => {
    setLiveNotification({ mensagem, pedidoId, imagem })
  }, [])
  const limparNotificacaoViva = useCallback(() => setLiveNotification(null), [])
  const [isValidSession, setIsValidSession] = useState(false)
  const [isSessionExpired, setIsSessionExpired] = useState(false)
  const [isOrderCompleted, setIsOrderCompleted] = useState(false)

  const [customerAuth, setCustomerAuth] = useState<CustomerAuth | null>(null)
  const isAuthenticated = !!customerAuth

  const [estabelecimentoId, setEstabelecimentoId] = useState<string | null>(null)
  const [completedOrders, setCompletedOrders] = useState<CompletedOrder[]>([])
  const [orderHistory, setOrderHistory] = useState<OrderHistory[]>([])

  // now we destructure the helper functions from the websocket hook too
  const {
    orderStatus: webSocketOrderStatus,
    isListening: isWebSocketListening,
    fetchCurrentStatus,
    refreshStatus,
  } = useOrderWebSocket(currentOrderId, localId || undefined, estabelecimentoId || undefined)

  // Carregar pedidos finalizados e histórico ao inicializar
  useEffect(() => {
    const completed = getCompletedOrders()
    setCompletedOrders(completed)

    const history = getOrderHistoryFromStorage()
    setOrderHistory(history)

    refreshDeviceOrders()
  }, [])

  const saveOrderHistoryToStorage = (history: OrderHistory[]) => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(ORDER_HISTORY_KEY, JSON.stringify(history))
      } catch (error) {
        console.error("[Context] Erro ao salvar histórico:", error)
      }
    }
  }

  const getOrderHistoryFromStorage = (): OrderHistory[] => {
    if (typeof window !== "undefined") {
      try {
        const data = localStorage.getItem(ORDER_HISTORY_KEY)
        return data ? JSON.parse(data) : []
      } catch (error) {
        console.error("[Context] Erro ao carregar histórico:", error)
        return []
      }
    }
    return []
  }


  // Função para adicionar ao histórico
  const addToOrderHistory = (order: OrderStatus, customerName: string) => {
    const historyItem: OrderHistory = {
      orderId: order._id,
      customerName,
      items: order.itens.map((item) => ({
        prato: {
          nome: item.prato.nome,
          preco: item.prato.preco,
        },
        quantidade: item.quantidade,
      })),
      total: order.itens.reduce((sum, item) => sum + item.prato.preco * item.quantidade, 0),
      status: order.status,
      date: order.createdAt,
      completedAt: new Date().toISOString(),
    }

    const newHistory = [historyItem, ...orderHistory].slice(0, 50)
    setOrderHistory(newHistory)
    saveOrderHistoryToStorage(newHistory)
  }

  // --- GLOBAL LISTENER PARA NOTIFICAÇÕES EM TEMPO REAL ---
  // Monitora todos os pedidos ativos (deviceOrders) e emite notificação push
  useEffect(() => {
    // Lista de orders IDs para monitorar
    const monitoredIds = deviceOrders.map(o => o.orderId)
    if (monitoredIds.length === 0) return

    console.log("[AppContextGlobal] 📡 Iniciando monitoramento global para:", monitoredIds)

    // Entrar nos canais de todos os pedidos ativos
    monitoredIds.forEach(id => {
      // Usa lógica do socket.ts para entrar na sala atualizarPedido_${id}
      joinOrderChannel(id, localId || undefined, estabelecimentoId || undefined)
    })

    // Handler genérico para atualizarPedidoId
    const handleGlobalUpdate = (data: any) => {
      console.log("[AppContextGlobal] 🔔 Evento global recebido:", data)
      const orderData = data.payload || data.data || data

      if (!orderData) return
      const updatedId = orderData._id || orderData.id

      // Verifica se é um dos nossos pedidos
      if (monitoredIds.includes(updatedId)) {
        console.log(`[AppContextGlobal] ✅ Atualização relevante para pedido ${updatedId}: ${orderData.status}`)

        // Disparar notificação visual
        const statusMsg: Record<string, string> = {
          "Pendente": "Pedido recebido",
          "Em preparo": "Pedido em preparo",
          "preparando": "Pedido em preparo",
          "Preparando": "Pedido em preparo",
          "Entregue": "Pedido entregue — bom apetite!",
          "Finalizado": "Pedido pronto!",
          "Cancelado": "Pedido cancelado"
        }
        const mensagem = statusMsg[orderData.status as string] || `Status: ${orderData.status}`

        // Buscar imagem do primeiro prato do pedido
        const deviceOrder = deviceOrders.find(o => o.orderId === updatedId)
        let dishImage: string | undefined
        if (deviceOrder?.items?.[0]) {
          const firstName = deviceOrder.items[0].prato?.nome
          if (firstName) {
            for (const cat of categories) {
              const found = cat.pratos?.find((p: any) => p.nome === firstName || p._id === firstName)
              if (found?.urls?.[0]) { dishImage = found.urls[0]; break }
            }
          }
        }

        // Notificação viva (glass, base) — clicar em "Ver" abre a página Agora
        mostrarNotificacaoViva(mensagem, updatedId, dishImage)

        // Atualizar estado local IMEDIATAMENTE antes de dar refresh
        updateOrderStatus(updatedId, orderData.status)

        // Atualizar estado (lê do localStorage que acabamos de atualizar)
        refreshDeviceOrders()

        // 🚀 Lógica de Redirecionamento e Notificação de Fundo
        if (orderData.status === "Finalizado" || orderData.status === "Entregue") {
          // Notificação do Navegador (Funciona mesmo minimizado)
          if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
            new Notification("Seu Pedido está Pronto!", {
              body: "Toque para ver os detalhes.",
              icon: "/icons/icon-192x192.png", // Ajuste conforme ícone existente
              tag: `order-${updatedId}`
            })
          }
          // Redirecionamento / Abrir Modal
          // O usuário pediu "redirecione na modal dele" -> abrir o modal do pedido
          setCurrentOrderId(updatedId)
          setShowOrderStatus(true)
        }
      }
    }

    // Registrar listener
    const cleanup = addEventListener("atualizarPedidoId", handleGlobalUpdate)

    return () => {
      console.log("[AppContextGlobal] 🛑 Limpando monitoramento global")
      cleanup()
      monitoredIds.forEach(id => {
        leaveOrderChannel(id, localId || undefined, estabelecimentoId || undefined)
      })
    }
  }, [deviceOrders, localId, estabelecimentoId])

  // WebSocket sync: process only if matches currentOrderId
  const lastStatusRef = useRef<string | null>(null)

  useEffect(() => {
    if (!webSocketOrderStatus) return
    if (!currentOrderId) return
    if (webSocketOrderStatus._id !== currentOrderId) return

    // Notificação de mudança de status
    if (lastStatusRef.current && lastStatusRef.current !== webSocketOrderStatus.status) {
      const msg = {
        Pendente: "Seu pedido foi recebido",
        "Em preparo": "Seu pedido está sendo preparado",
        preparando: "Seu pedido está sendo preparado",
        Preparando: "Seu pedido está sendo preparado",
        Entregue: "Pedido entregue — bom apetite!",
        Finalizado: "Seu pedido foi finalizado",
        Cancelado: "Seu pedido foi cancelado",
      }[webSocketOrderStatus.status] || `Status do pedido: ${webSocketOrderStatus.status}`

      // Notificação viva (glass, topo) — clicar em "Ver" abre a página Agora
      mostrarNotificacaoViva(msg, webSocketOrderStatus._id)

      showNotification(msg)

      // Browser Push Notification
      if (typeof window !== "undefined" && "Notification" in window) {
        if (Notification.permission === "granted") {
          new Notification("Atualização do Pedido", {
            body: msg,
            icon: "/placeholder-logo.png",
          })
        } else if (Notification.permission !== "denied") {
          Notification.requestPermission().then((permission) => {
            if (permission === "granted") {
              new Notification("Atualização do Pedido", {
                body: msg,
                icon: "/placeholder-logo.png",
              })
            }
          })
        }
      }
    }
    lastStatusRef.current = webSocketOrderStatus.status

    setOrderStatus(webSocketOrderStatus)
    updateOrderStatus(webSocketOrderStatus._id, webSocketOrderStatus.status)

    // VERIFICAÇÃO CRÍTICA: não reabrir se já foi fechado manualmente
    const wasManuallyClosed = manuallyClosedOrdersRef.current.has(webSocketOrderStatus._id)

      if (
        (webSocketOrderStatus.status === "Entregue" ||
          webSocketOrderStatus.status === "Finalizado") &&
        !acknowledgedCompletedRef.current.has(webSocketOrderStatus._id) &&
        !wasManuallyClosed // ✅ Adicionado esta condição
      ) {
        acknowledgedCompletedRef.current.add(webSocketOrderStatus._id)
        persistAcknowledged()
        setIsOrderCompleted(true)

        removeActiveOrder(webSocketOrderStatus._id)

        if (customerAuth) {
          // O payload do socket pode não trazer itens populados — nunca assumir
          const itensSeguros = Array.isArray(webSocketOrderStatus.itens)
            ? webSocketOrderStatus.itens
            : []
          const completed: CompletedOrder = {
            orderId: webSocketOrderStatus._id,
            customerName: customerAuth.name,
            total: itensSeguros.reduce(
              (s, i) => s + (i?.prato?.preco || 0) * (i?.quantidade || 0),
              0,
            ),
            completedAt: new Date().toISOString(),
            items: itensSeguros,
          }

        saveCompletedOrder(completed)
        setCompletedOrders((p) => [...p, completed])
        addToOrderHistory(webSocketOrderStatus, customerAuth.name)
      }
    }
  }, [webSocketOrderStatus, currentOrderId, customerAuth])

  const saveOrderData = (orderId: string, status: OrderStatus) => {
    if (typeof window !== "undefined") {
      try {
        const orderData = {
          orderId,
          status,
          timestamp: Date.now(),
        }
        localStorage.setItem(ORDER_DATA_KEY, JSON.stringify(orderData))
      } catch (error) {
        console.error("[AppContext] Erro ao salvar dados do pedido:", error)
      }
    }
  }

  const loadOrderData = () => {
    if (typeof window === "undefined") return
    const data = localStorage.getItem(ORDER_DATA_KEY)
    if (!data) return

    const parsed = JSON.parse(data)
    setCurrentOrderId(parsed.orderId)
    setOrderStatus(parsed.status)

    if (
      parsed.status.status === "Entregue" ||
      parsed.status.status === "Finalizado"
    ) {
      acknowledgedCompletedRef.current.add(parsed.orderId)
      persistAcknowledged()
    }
  }

  // Limpar dados do pedido
  const clearOrderData = () => {
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem(ORDER_DATA_KEY)
      } catch (error) {
        console.error("[AppContext] Erro ao limpar dados do pedido:", error)
      }
    }
  }

  // Inicialização do contexto
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const closedOrders = localStorage.getItem("manually_closed_orders")
        if (closedOrders) {
          const orders = JSON.parse(closedOrders)
          manuallyClosedOrdersRef.current = new Set(orders)
        }
      } catch (e) {
        console.error("[AppContext] Erro ao restaurar ordens fechadas:", e)
      }
    }
  }, [])

  // Carregar e validar token salvo na inicialização
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search)
      const urlLocalId = params.get("localId")
      const urlEstabelecimentoId = params.get("estabelecimentoId")
      const urlMenuId = params.get("menuId")

      const savedToken = getTokenFromStorage()
      let currentToken = savedToken
      let payload = currentToken ? validateEncryptedToken(currentToken) : null

      // Se temos novos parâmetros na URL, devemos criar um novo token ou validar o existente
      if (urlLocalId && urlEstabelecimentoId) {
        console.log("[Context] Novos parâmetros detectados na URL:", { urlLocalId, urlEstabelecimentoId, urlMenuId })

        // Se já temos um token, verificar se ele corresponde aos novos parâmetros e não expirou
        if (payload && !isTokenExpired(currentToken!) && !isTokenMarkedAsExpired() &&
          payload.localId === urlLocalId && payload.estabelecimentoId === urlEstabelecimentoId) {
          console.log("[Context] Token existente ainda é válido para estes parâmetros")
        } else {
          // Caso contrário, gerar um novo token
          console.log("[Context] Gerando novo token para os novos parâmetros")
          currentToken = generateEncryptedToken(urlLocalId, urlEstabelecimentoId, urlMenuId || undefined)
          saveTokenToStorage(currentToken)
          payload = validateEncryptedToken(currentToken)
        }

        // Limpar a URL após processar os parâmetros para não ficarem expostos
        redirectToCleanURL(currentToken!)
      } else if (savedToken) {
        // Se não temos parâmetros na URL mas temos um token salvo, validar
        if (payload && !isTokenExpired(savedToken) && !isTokenMarkedAsExpired()) {
          console.log("[Context] Usando token existente válido")
        } else {
          console.log("[Context] Token existente expirado ou inválido")
          markTokenAsExpired()
          setIsSessionExpired(true)
          setIsValidSession(false)
          return
        }
      }

      // Se temos um payload válido (seja do novo token ou do antigo)
      if (payload) {
        setToken(currentToken)
        setLocalId(payload.localId)
        setEstabelecimentoId(payload.estabelecimentoId || null)
        setMenuId(payload.menuId || sessionStorage.getItem("qm_menuId"))
        setIsValidSession(true)
        setIsSessionExpired(false)

        // Tentar restaurar dados adicionais da sessão
        const sessionData = getSessionData()
        if (sessionData && (sessionData.localId === payload.localId)) {
          if (sessionData.customerAuth) setCustomerAuth(sessionData.customerAuth)
          if (sessionData.draftItems?.length > 0) setDraftItems(sessionData.draftItems)
          if (sessionData.currentOrderId) setCurrentOrderId(sessionData.currentOrderId)
          if (sessionData.menuId) setMenuId(sessionData.menuId)
        }
      } else if (!urlLocalId) {
        // Se não há token nem parâmetros, a sessão não é válida
        setIsValidSession(false)
      }
    }
  }, [])

  useEffect(() => {
    if (localId && (customerAuth || currentOrderId || draftItems.length > 0)) {
      const sessionData: SessionData = {
        localId,
        estabelecimentoId: estabelecimentoId || undefined,
        menuId: menuId || undefined,
        customerAuth,
        currentOrderId,
        draftItems,
        lastActivity: Date.now(),
      }
      saveSessionData(sessionData)
    }
  }, [localId, estabelecimentoId, menuId, customerAuth, currentOrderId, draftItems])

  const resetToken = () => {
    markTokenAsExpired()
    setIsSessionExpired(true)
    setIsValidSession(false)
  }

  useEffect(() => {
    if (localId && isValidSession) {
      setIsLoading(true)
      setError(null)

      const fetchData = async () => {
        try {
          const data = await fetchMenu(localId, menuId)

          // Auto-injetar estabelecimentoId se faltar, para garantir que o socket entre na sala correta
          if (!estabelecimentoId && data.length > 0) {
            const firstDishWithId = data.find(c => c.pratos && c.pratos.length > 0)?.pratos[0]
            if (firstDishWithId?.estabelecimentoId) {
              console.log("[Context] EstabelecimentoId recuperado do menu:", firstDishWithId.estabelecimentoId)
              setEstabelecimentoId(firstDishWithId.estabelecimentoId)
            }
          }

          const translatedData = data.map((category) => ({
            ...category,
            nome: translateCategoryName(category.nome, language),
            pratos: category.pratos.map((dish) => ({
              ...dish,
              nome: translateDishName(dish.nome, language),
            })),
          }))
          const bebidas = await fetchMenuBebidas(localId, menuId)

          const translatedDataBebidas = bebidas.map((bebida) => ({
            ...bebida,
            nome: translateCategoryName(bebida.nome, language),
            pratos: bebida.pratos.map((dish) => ({
              ...dish,
              nome: translateDishName(dish.nome, language),
            })),
          }))

          setCategories(translatedData)
          setCategoriesBebidas(translatedDataBebidas)
          setIsLoading(false)
        } catch (err: any) {
          setError(err.message)
          setIsLoading(false)
        }
      }

      fetchData()

      // Fallback: endpoint dedicado (não sobrescreve se já veio no payload)
      fetchBuffete(localId, menuId).then((b) => {
        if (b) setBuffete(b)
      })
    }
  }, [localId, menuId, language, isValidSession])

  // Buscar dados do estabelecimento
  useEffect(() => {
    if (localId && isValidSession) {
      const fetchEstabelecimentoData = async () => {
        try {
          console.log("[Context] Buscando dados do estabelecimento...")
          const data = await fetchEstabelecimento(localId, menuId)
          if (data) {
            setEstabelecimento(data)

            // O bufete anunciado viaja no payload do estabelecimento (fonte
            // primária — funciona mesmo quando /generic/buffete falha)
            const bufeteDoPayload = (data as any).bufete
            if (bufeteDoPayload) setBuffete(bufeteDoPayload)
          } else {
            console.warn("[Context] Nenhum dado de estabelecimento retornado")
          }
        } catch (err: any) {
          console.error("[Context] Erro ao buscar dados do estabelecimento:", err)
        }
      }

      fetchEstabelecimentoData()
    }
  }, [localId, menuId, isValidSession])

  useEffect(() => {
    if (categories.length > 0) {
      const translatedData = categories.map((category) => ({
        ...category,
        nome: translateCategoryName(category.nome, language),
        pratos: category.pratos.map((dish) => ({
          ...dish,
          nome: translateDishName(dish.nome, language),
        })),
      }))

      setCategories(translatedData)
    }
    if (categoriesBebidas.length > 0) {
      const translatedData = categoriesBebidas.map((category) => ({
        ...category,
        nome: translateCategoryName(category.nome, language),
        pratos: category.pratos.map((dish) => ({
          ...dish,
          nome: translateDishName(dish.nome, language),
        })),
      }))

      setCategoriesBebidas(translatedData)
    }
  }, [language])

  const t = (key: string): string => {
    return translations[language]?.[key] || key
  }

  const playAddSound = () => {
    if (cachedAddSound) {
      try {
        const soundClone = cachedAddSound.cloneNode() as HTMLAudioElement
        soundClone.volume = 0.3
        soundClone.play().catch((e) => console.error("Erro ao tocar som:", e))
      } catch (error) {
        console.error("Erro ao clonar áudio:", error)
      }
    }
  }

  const showNotification = (message: string) => {
    setNotification({
      message,
      isVisible: true,
    })
  }

  const hideNotification = () => {
    setNotification((prev) => ({
      ...prev,
      isVisible: false,
    }))
  }

  const addToDraft = (dish: Dish) => {
    setTimeout(() => playAddSound(), 0)

    setDraftItems((prev) => {
      const existingItem = prev.find((item) => item.dish._id === dish._id)

      setTimeout(() => {
        showNotification(existingItem ? t("quantityIncreased") : t("itemAdded"))
      }, 0)

      if (existingItem) {
        return prev.map((item) => (item.dish._id === dish._id ? { ...item, quantity: item.quantity + 1 } : item))
      } else {
        return [...prev, { dish, quantity: 1 }]
      }
    })
  }


  // Remover item do rascunho
  const removeDraftItem = (dishId: string) => {
    setDraftItems((prev) => prev.filter((item) => item.dish._id !== dishId))
  }

  const updateDraftItemQuantity = (dishId: string, quantity: number) => {
    if (quantity <= 0) {
      removeDraftItem(dishId)
      return
    }

    setDraftItems((prev) => prev.map((item) => (item.dish._id === dishId ? { ...item, quantity } : item)))
  }

  const clearDraft = () => {
    setDraftItems([])
  }

  const addBuffeteToDraft = () => {
    if (!buffete) return
    setTimeout(() => playAddSound(), 0)
    setDraftBuffeteQty((prev) => prev + 1)
    setTimeout(() => showNotification("Bufete adicionado ao pedido!"), 0)
  }

  const updateDraftBuffeteQuantity = (quantity: number) => {
    setDraftBuffeteQty(Math.max(0, quantity))
  }

  const removeBuffeteFromDraft = () => {
    setDraftBuffeteQty(0)
  }

  const draftCount = draftItems.reduce((sum, item) => sum + item.quantity, 0) + draftBuffeteQty
  const draftTotal =
    draftItems.reduce((sum, item) => sum + effectivePrice(item.dish) * item.quantity, 0) +
    (buffete ? buffete.preco * draftBuffeteQty : 0)

  const submitCustomerOrder = async (
    customerName: string,
    phoneNumber: string,
    paymentMethod: string,
    dadosFiscais?: { tipo: "pessoal" | "empresa"; nif?: string; nomeEmpresa?: string; endereco?: string },
  ) => {
    if (!localId) {
      throw new Error("ID do local não disponível")
    }

    // Gerar token se ainda não existir
    let currentToken = token
    if (!currentToken) {
      currentToken = generateEncryptedToken(localId, estabelecimentoId || undefined, menuId || undefined)
      setToken(currentToken)
    }

    const orderItems: OrderItem[] = draftItems.map((item) => ({
      prato: item.dish._id,
      quantidade: item.quantity,
    }))

    // Login automático por telefone: encontra/cria o cliente e guarda o id
    let clienteId = customerAuth?.clienteId
    try {
      const sessao = await iniciarSessaoCliente(phoneNumber, customerName)
      if (sessao?._id) clienteId = sessao._id
    } catch (e) {
      console.warn("[Context] Sessão de cliente falhou, pedido segue sem clienteId:", e)
    }

    const orderBuffetes: OrderBuffete[] | undefined =
      draftBuffeteQty > 0 && buffete ? [{ buffeteId: buffete._id, quantidade: draftBuffeteQty }] : undefined

    const order = {
      nome_cliente: customerName,
      telefone: phoneNumber,
      itens: orderItems,
      buffetes: orderBuffetes,
      paymentMethod: paymentMethod,
      observacoes: orderObservacoes.trim() || undefined,
      clienteId: clienteId || undefined,
      localAtendimentoId: localId,
      token: currentToken,
      // Faturação escolhida no checkout (pessoal ou empresa com NIF)
      dadosFiscais:
        dadosFiscais?.tipo === "empresa"
          ? { tipo: "empresa", nif: dadosFiscais.nif, nomeEmpresa: dadosFiscais.nomeEmpresa, endereco: dadosFiscais.endereco }
          : { tipo: "pessoal" },
    }

    let result = null
    try {
      // 1. Solicitar permissão de notificação (User Gesture)
      if (typeof window !== "undefined" && "Notification" in window) {
        if (Notification.permission === "default") {
          await Notification.requestPermission()
        }
      }

      // 2. Enviar pedido
      result = await submitOrder(order)
    } catch (error) {
      console.error("[AppContext] Erro ao enviar pedido:", error)
      throw error
    }

    if (result && result._id) {
      // Notificação imediata de pedido recebido (estado Pendente)
      try {
        if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
          new Notification("Pedido recebido!", {
            body: "A cozinha já tem o teu pedido. Enquanto esperas, aproveita o momento...",
            icon: "/placeholder-logo.png",
            tag: `pending-${result._id}`,
          })
        }
      } catch (e) {
        console.warn("[Context] Notificação de pendente falhou:", e)
      }

      setActiveOrderIds((prev) => (prev.includes(result._id) ? prev : [...prev, result._id]))

      const deviceOrder: DeviceOrder = {
        orderId: result._id,
        customerName,
        customerPhone: phoneNumber,
        items: [
          ...draftItems.map((item) => ({
            prato: {
              nome: item.dish.nome,
              preco: item.dish.preco,
              descricao: item.dish.descricao,
            },
            quantidade: item.quantity,
          })),
          // O bufete aparece no histórico como linha própria
          ...(buffete && draftBuffeteQty > 0
            ? [{
                prato: {
                  nome: `Bufete · ${buffete.nome}`,
                  preco: buffete.preco,
                  descricao: `${buffete.quantidadePratos} pratos incluídos`,
                },
                quantidade: draftBuffeteQty,
              }]
            : []),
        ],
        total: draftTotal,
        status: result.status || "Pendente",
        paymentMethod,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        localId: localId!,
        estabelecimentoId: estabelecimentoId || undefined,
      }

      addOrderToDevice(deviceOrder)
      refreshDeviceOrders()

      setCurrentOrderId(result._id)
      setOrderStatus(result)
      setShowOrderStatus(true)
      setIsOrderCompleted(false)

      saveOrderData(result._id, result)

      const newCustomerAuth: CustomerAuth = {
        name: customerName,
        phoneNumber: phoneNumber,
        clienteId,
        lastOrderDate: new Date().toISOString(),
      }

      setCustomerAuth(newCustomerAuth)

      // Token já foi gerado antes do submit
      saveTokenToStorage(currentToken)
      clearTokenExpiredMark()

      redirectToCleanURL(currentToken)

      setIsValidSession(true)
      setIsSessionExpired(false)
    }

    clearDraft()
    setDraftBuffeteQty(0)
    setOrderObservacoes("")
    return result
  }

  const addActiveOrder = (orderId: string) => {
    setActiveOrderIds((prev) => [...prev, orderId])
  }

  const removeActiveOrder = (orderId: string) => {
    setActiveOrderIds((prev) => prev.filter((id) => id !== orderId))
  }

  const refreshDeviceOrders = () => {
    const orders = getAllDeviceOrders()
    setDeviceOrders((prev) => {
      const prevIds = prev.map(o => o.orderId).join(',')
      const newIds = orders.map(o => o.orderId).join(',')
      if (prevIds === newIds) return prev
      return orders
    })

    const activeOrders = getActiveOrders()
    const newActive = activeOrders.map((order) => order.orderId)
    setActiveOrderIds((prev) => {
      if (prev.length === newActive.length && prev.every((v, i) => v === newActive[i])) {
        return prev
      }
      return newActive
    })
  }

  const fetchOrderStatus = useCallback(async () => {
    if (currentOrderId) {
      try {
        if (typeof fetchCurrentStatus === "function") {
          const status = await fetchCurrentStatus()
          if (status) {
            setOrderStatus(status)
            saveOrderData(currentOrderId, status)
            if (status.status === "Entregue" || status.status === "Finalizado") {
              setIsOrderCompleted(true)
            }
          }
        }
      } catch (error) {
        console.error("[AppContext] ❌ Erro ao buscar status do pedido:", error)
      }
    }
  }, [currentOrderId, fetchCurrentStatus])

  const refreshOrderStatus = async (): Promise<OrderStatus | null> => {
    if (currentOrderId) {
      try {
        if (typeof refreshStatus === "function") {
          const status = await refreshStatus()
          if (status) {
            setOrderStatus(status)
            saveOrderData(currentOrderId, status)
            if (status.status === "Entregue" || status.status === "Finalizado") {
              setIsOrderCompleted(true)
            }
          }
          return status
        }
      } catch (error) {
        console.error("[AppContext] ❌ Erro ao atualizar status do pedido:", error)
        return null
      }
    }
    return null
  }

  const completeOrder = useCallback(() => {
    if (currentOrderId) {
      // Marcar como fechado manualmente para prevenir reabertura
      manuallyClosedOrdersRef.current.add(currentOrderId)
      // Persistir no localStorage
      if (typeof window !== "undefined") {
        try {
          const closedOrders = Array.from(manuallyClosedOrdersRef.current)
          localStorage.setItem("manually_closed_orders", JSON.stringify(closedOrders))
        } catch (e) {
          console.error("[AppContext] Erro ao salvar ordens fechadas:", e)
        }
      }

      acknowledgeOrder(currentOrderId)
      removeActiveOrder(currentOrderId)
    }

    setCurrentOrderId(null)
    setOrderStatus(null)
    setIsOrderCompleted(false)
    setShowOrderStatus(false)
    setDraftItems([])
    setDraftBuffeteQty(0)
  }, [currentOrderId])


  // Função para fazer logout
  const logout = () => {
    clearOrderData()
    acknowledgedCompletedRef.current.clear()
    try {
      if (typeof window !== "undefined") localStorage.removeItem(ACK_COMPLETED_KEY)
    } catch (e) {
      console.error("[AppContext] Erro ao limpar acknowledged do storage:", e)
    }
    setCustomerAuth(null)
    setCurrentOrderId(null)
    setOrderStatus(null)
    setDraftItems([])
    setIsOrderCompleted(false)
    setCompletedOrders([])
  }


  // Função para obter histórico de pedidos finalizados
  const getCompletedOrdersHistory = (): CompletedOrder[] => {
    return completedOrders
  }

  // Verificação automática de expiração do token - DESABILITADA POR ENQUANTO
  // useEffect(() => {
  //   if (!isValidSession || !token) return

  //   const checkExpiration = () => {
  //     const expired = checkTokenExpirationAndCleanup()
  //     if (expired && isValidSession) {
  //       setIsSessionExpired(true)
  //       setIsValidSession(false)
  //       setToken(null)
  //       setLocalId(null)
  //       setEstabelecimentoId(null)
  //       setCustomerAuth(null)
  //       setCurrentOrderId(null)
  //       setOrderStatus(null)
  //       setDraftItems([])
  //       setIsOrderCompleted(false)
  //     }
  //   }

  //   checkExpiration()

  //   const interval = setInterval(checkExpiration, 30000)

  //   return () => clearInterval(interval)
  // }, [isValidSession, token])

  return (
    <AppContext.Provider
      value={{
        language,
        setLanguage,
        t,
        draftItems,
        addToDraft,
        removeDraftItem,
        updateDraftItemQuantity,
        clearDraft,
        draftCount,
        draftTotal,
        orderObservacoes,
        setOrderObservacoes,
        buffete,
        draftBuffeteQty,
        addBuffeteToDraft,
        updateDraftBuffeteQuantity,
        removeBuffeteFromDraft,
        showDraft,
        setShowDraft,
        notification,
        showNotification,
        hideNotification,
        playAddSound,
        categories,
        categoriesBebidas,
        isLoading,
        error,
        localId,
        setLocalId,
        submitCustomerOrder,
        showCustomerNameModal,
        setShowCustomerNameModal,
        token,
        resetToken,
        activeOrderIds,
        addActiveOrder,
        removeActiveOrder,
        deviceOrders,
        refreshDeviceOrders,
        orderStatus,
        fetchOrderStatus,
        showOrderStatus,
        setShowOrderStatus,
        liveNotification,
        mostrarNotificacaoViva,
        limparNotificacaoViva,
        isValidSession,
        customerAuth,
        acknowledgeOrder,
        isOrderAcknowledged,
        setCustomerAuth,
        isAuthenticated,
        logout,
        isSessionExpired,
        completeOrder,
        isOrderCompleted,
        isWebSocketListening,
        webSocketOrderStatus,
        refreshOrderStatus,
        estabelecimentoId,
        completedOrders,
        getCompletedOrdersHistory,
        orderHistory,
        addToOrderHistory,
        getOrderHistoryFromStorage,
        currentOrderId,
        setCurrentOrderId,
        estabelecimento,
        setEstabelecimento,
        menuId,
      }}
    >
      {children}
    </AppContext.Provider>

  )
}